import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useProducts } from "@/context/ProductsContext";

const CartContext = createContext(null);
const STORAGE_KEY = "dna_cart_items";

const loadStoredItems = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const CartProvider = ({ children }) => {
  const { products } = useProducts();
  const [items, setItems] = useState(loadStoredItems);
  const [open, setOpen] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (id) => {
    const p = products.find((x) => x.slug === id);
    if (!p) return;
    setItems((prev) => ({
      ...prev,
      [id]: {
        key: id,
        id,
        kind: "single",
        productSlug: p.slug,
        name: p.name,
        flavor: p.flavor,
        price: p.price,
        image: p.image,
        subscription: false,
        qty: (prev[id]?.qty || 0) + 1,
      },
    }));
    setOpen(true);
  };

  const addBox = ({ size, collagen, creatine, subscription, price }) => {
    const key = `box-${size}-${collagen}-${creatine}-${subscription ? "sub" : "once"}`;
    const collagenProduct = products.find((p) => p.slug === "collagen-bar");
    const creatineProduct = products.find((p) => p.slug === "creatine-bar");
    setItems((prev) => ({
      ...prev,
      [key]: {
        key,
        id: key,
        kind: "box",
        size,
        collagenQty: collagen,
        creatineQty: creatine,
        name: `DNA Custom Box of ${size}${subscription ? " · Monthly" : ""}`,
        flavor: `${collagen}× Collagen Bar · ${creatine}× Creatine Bar`,
        price,
        image: collagen >= creatine ? collagenProduct?.image : creatineProduct?.image,
        subscription,
        qty: (prev[key]?.qty || 0) + 1,
      },
    }));
    setOpen(true);
  };

  const setQty = (key, qty) =>
    setItems((p) => {
      const n = { ...p };
      if (qty <= 0) delete n[key];
      else n[key] = { ...n[key], qty };
      return n;
    });

  const clear = () => setItems({});

  const detailed = Object.values(items);
  const total = detailed.reduce((s, i) => s + i.price * i.qty, 0);
  const count = detailed.reduce((s, i) => s + i.qty, 0);

  const value = useMemo(
    () => ({ detailed, total, count, add, addBox, setQty, clear, open, setOpen, suggestion, setSuggestion }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, open, suggestion]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
